import { getBoostSubscription } from './subscriptions';
import { getBoostTrial } from './trials';
import { BoostSubscription, BoostTrial } from './types';

export const getBoostSubscriptionAndTrialInfo = async (
    activeOrganizationId?: string,
) => {
    const getSub = async () => {
        let subscription;

        try {
            subscription = await getBoostSubscription();
        } catch (e) {
            console.log('Error getting subscription:', e);
            subscription = null;
        }

        return subscription as BoostSubscription;
    };

    const getTrial = async () => {
        let trial;

        try {
            trial = await getBoostTrial(activeOrganizationId);
        } catch (e) {
            console.log('Error getting trial:', e);
            trial = null;
        }

        return trial as BoostTrial;
    };

    const [subscription, trial] = await Promise.all([getSub(), getTrial()]);

    // If an org has no trial record or an expired trial record (their trial ended 30+ days ago), they are eligible to start a new trial (provided they don't have an active non-Lite subscription)
    const canStartNewTrial = !trial || trial.status === 'expired';

    // These plans encompass everything offered in a trial, which is essentially a new-basic-monthly package - so orgs with these subs should not start a trial as it's pointless
    const isActiveCoreOrProSubscription = (subscription: BoostSubscription) =>
        subscription &&
        ['active', 'past_due'].includes(subscription.status) &&
        subscription.plan_id &&
        [
            'new-basic-monthly',
            'new-basic-yearly',
            'new-pro-monthly',
            'new-pro-yearly',
        ].includes(subscription.plan_id);

    // Some orgs may be on old and/or custom plans, in which case we don't want to prompt them to start a trial
    const isActiveCustomSubscription = (subscription: BoostSubscription) =>
        subscription &&
        ['active', 'past_due'].includes(subscription.status) &&
        subscription.plan_id &&
        ![
            'new-starter-monthly',
            'new-starter-yearly',
            'new-basic-monthly',
            'new-basic-yearly',
            'new-pro-monthly',
            'new-pro-yearly',
            'trialing',
        ].includes(subscription.plan_id);

    const isCurrentlyPayingForPlan = (subscription: BoostSubscription) => {
        if (!subscription) {
            return false;
            // Status of active or past_due always means user is subscribed
        } else if (['active', 'past_due'].includes(subscription.status)) {
            return true;
            // At present if a user is subscribed to Lite but starts a trial, their
            // sub record will show as plan_id = 'new-starter-monthly/yearly' && status = 'trialing',
            // so in this case they ARE currently subscribed
        } else if (
            subscription.plan_id &&
            ['new-starter-monthly', 'new-starter-yearly'].includes(
                subscription.plan_id,
            ) &&
            subscription.status === 'trialing'
        ) {
            return true;
        } else {
            return false;
        }
    };

    let isEligibleForTrial = false;
    let isTrialing = false;
    const isCurrentlySubscribed = isCurrentlyPayingForPlan(subscription);

    if (
        canStartNewTrial &&
        !(
            isActiveCoreOrProSubscription(subscription) ||
            isActiveCustomSubscription(subscription)
        )
    ) {
        isEligibleForTrial = true;
    }

    if (
        trial?.status === 'trialing' &&
        !(
            isActiveCoreOrProSubscription(subscription) ||
            isActiveCustomSubscription(subscription)
        )
    ) {
        isTrialing = true;
    }

    return {
        subscription,
        trial,
        is_currently_subscribed: isCurrentlySubscribed,
        is_trial_eligible: isEligibleForTrial,
        is_trialing: isTrialing,
    };
};

export * from './constants';
export * from './customers';
export * from './pp-mocks';
export * from './subscriptions';
export * from './trials';
export * from './types';
